import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Typography, Box, Stack, Paper, Grid, Checkbox, FormControlLabel, FormHelperText, Alert, Divider } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAuth } from '../contexts/AuthContext';
import DemoEmailModal from './DemoEmailModal';
import linesOverlay from '../assets/images/Lines-no_background.png';
import agentImage from '../assets/images/Agent.png';
import femaleAgentImage from '../assets/images/Female-agent.png';

const Login = () => {
  const { login, loginDemo, authError, clearAuthError } = useAuth();
  const navigate = useNavigate();
  const [consentChecked, setConsentChecked] = useState(true);
  const [consentError, setConsentError] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  // Randomly choose agent image (50/50 chance)
  const selectedAgentImage = Math.random() < 0.5 ? agentImage : femaleAgentImage;

  useEffect(() => {
    // Add login-page class to html and body for blue overscroll
    document.documentElement.classList.add('login-page');
    document.body.classList.add('login-page');

    // Track invite link clicks
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('invite_token');

    if (inviteToken) {
      // Store token in sessionStorage to pass through OAuth redirect
      sessionStorage.setItem('invite_token', inviteToken);

      // Send GA event for link click
      if (window.gtag) {
        window.gtag('event', 'invite_link_clicked', {
          invite_token: inviteToken
        });
      }
    }

    // Clean up when component unmounts
    return () => {
      document.documentElement.classList.remove('login-page');
      document.body.classList.remove('login-page');
    };
  }, []);

  const handleMicrosoftLogin = () => {
    if (!consentChecked) {
      setConsentError(true);
      return;
    }
    setConsentError(false);
    login('aad'); // Azure AD login
  };

  const handleGoogleLogin = () => {
    if (!consentChecked) {
      setConsentError(true);
      return;
    }
    setConsentError(false);
    login('google'); // Google login
  };

  const handleConsentChange = (event) => {
    setConsentChecked(event.target.checked);
    if (event.target.checked) {
      setConsentError(false);
    }
  };

  const handleDemoSuccess = (demoData, prospectEmail) => {
    setDemoModalOpen(false);
    loginDemo(demoData, prospectEmail);
    navigate('/');
  };

  return (
    <Box
      sx={{
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        marginTop: { xs: '-2rem', md: '-3rem' },
        marginBottom: { xs: '-1rem', md: '-2rem' },
        width: '100vw',
        minHeight: 'calc(100vh - 80px)', // Account for header only
        backgroundColor: '#021c2d',
        backgroundImage: `url(${linesOverlay})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'flex-start',
        paddingTop: { xs: '2rem', md: '4rem' },
        py: { xs: 4, md: 8 },
        px: { xs: 2, md: 4 }
      }}
    >
      <Grid container spacing={4} alignItems="center" maxWidth="1200px" sx={{ mx: 'auto', gap: '80px' }}>
        <Grid item xs={12} md={7}>
          <Box sx={{ color: 'white', pr: { md: 4 } }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontFamily: 'Bebas Neue',
                fontWeight: 'normal',
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 3,
                lineHeight: 1.1
              }}
            >
              SUPERVOIMA AI EVALUATOR
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                mb: 3,
                fontWeight: 'normal'
              }}
            >
              Evaluate the State of AI across your entire organization and teams to prepare for future!
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
              In today's market, you need to know exactly where you stand. Our AI Audit provides the definitive clarity you need about your company's state of AI and next steps.
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              By giving it a try, you'll get more than just a score, you'll receive a strategic diagnostic of your company's AI readiness, actionable insights, and a set of expert recommendations. This is the crucial first step to ensuring you are leading the market, not just competing in it.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', ml: { md: '100px' } }}>
            <Paper
              elevation={8}
              sx={{
                p: 4,
                borderRadius: 3,
                textAlign: 'center',
                maxWidth: 400,
                width: '100%',
                backgroundColor: 'white',
                zIndex: 2
              }}
            >
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontFamily: 'Bebas Neue',
                  fontWeight: 'normal',
                  color: '#333',
                  mb: 2
                }}
              >
                LOGIN TO START ASSESSMENT
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                Sign in with your account to access our team of AI agents.
              </Typography>

              {authError && (
                <Alert
                  severity="error"
                  onClose={clearAuthError}
                  sx={{ mb: 2, maxWidth: 320, mx: 'auto' }}
                >
                  {authError}
                </Alert>
              )}

              <Stack spacing={2} sx={{ maxWidth: 320, mx: 'auto' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGoogleLogin}
                  sx={{
                    backgroundColor: '#2f2f2f',
                    color: 'white',
                    '&:hover': { backgroundColor: '#404040' },
                    py: 1.5,
                    px: 3,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    width: '100%',
                    justifyContent: 'flex-start',
                    gap: 2
                  }}
                  startIcon={
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg"
                      alt="Google"
                      style={{ width: '20px', height: '20px' }}
                    />
                  }
                >
                  Sign in with Google
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleMicrosoftLogin}
                  sx={{
                    backgroundColor: '#2f2f2f',
                    color: 'white',
                    '&:hover': { backgroundColor: '#404040' },
                    py: 1.5,
                    px: 3,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    width: '100%',
                    justifyContent: 'flex-start',
                    gap: 2
                  }}
                  startIcon={
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                      alt="Microsoft"
                      style={{ width: '20px', height: '20px' }}
                    />
                  }
                >
                  Sign in with Microsoft
                </Button>

                <Divider sx={{ my: 1 }}>or</Divider>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setDemoModalOpen(true)}
                  startIcon={<PlayArrowIcon />}
                  sx={{
                    py: 1.5,
                    px: 3,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    width: '100%',
                    borderColor: '#2f2f2f',
                    color: '#2f2f2f',
                    '&:hover': {
                      borderColor: '#404040',
                      backgroundColor: 'rgba(47, 47, 47, 0.04)'
                    }
                  }}
                >
                  Try Demo (limited features)
                </Button>
              </Stack>

              <Box sx={{ mt: 2, maxWidth: 320, mx: 'auto' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consentChecked}
                      onChange={handleConsentChange}
                      sx={{
                        color: consentError ? '#f44336' : 'rgba(0, 0, 0, 0.6)',
                        '&.Mui-checked': {
                          color: consentError ? '#f44336' : '#1976d2',
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: consentError ? '#f44336' : 'rgba(0, 0, 0, 0.23)',
                          },
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      I have read and agree to the {' '}
                      <a
                        href="https://www.supervoima.ai/privacy-statement"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'none' }}
                      >
                        Privacy Policy
                      </a>{''}.
                    </Typography>
                  }
                />
                {consentError && (
                  <FormHelperText error sx={{ fontSize: '0.7rem', mt: 0.5 }}>
                    Please agree to the Privacy Policy to continue.
                  </FormHelperText>
                )}
              </Box>

            </Paper>

            <DemoEmailModal
              open={demoModalOpen}
              onClose={() => setDemoModalOpen(false)}
              onSuccess={handleDemoSuccess}
            />

            {/* Agent Character */}
            <Box
              sx={{
                position: 'absolute',
                right: { md: -400, lg: -400, xl: -400 },
                bottom: { md: -80, lg: -100 },
                zIndex: 3,
                display: { xs: 'none', md: 'block' }
              }}
            >
              <img
                src={selectedAgentImage}
                alt="AI Agent Character"
                style={{
                  width: '400px',
                  height: 'auto',
                  filter: 'drop-shadow(0 6px 20px rgba(0,0,0,0.4))'
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;
